import { Title, Text, Group, Stack, Stepper, PasswordInput, Grid } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import { useUserData } from 'hooks/api/useUser';
import { displayNotificationError } from 'utils/notifications';
import { Trans, useTranslation } from 'react-i18next';
import { RecoveryCodesMethod } from '@extrahorizon/javascript-sdk';
import { PortalButton } from 'components/Button/PortalButton';
import { validateRequired } from 'utils/validation';
import { useDrawerContext } from 'context/hooks';
import { ConfirmPresenceResult, useConfirmPresence } from 'hooks/api/user/mfa/useConfirmPresence';
import { useVerifyMfa } from 'hooks/api/user/mfa/useVerifyMfa';
import { TwoStepAuthEnableList } from './TwoStepAuthEnableList';

export const TwoStepAuthEnable = () => {
  const { t } = useTranslation();
  const { data: userData } = useUserData();
  const [activeStep, setActiveStep] = useState(0);
  const [confirmPresenceToken, setConfirmPresenceToken] = useState('');
  const [methodId, setMethodId] = useState('');
  const [qrSecret, setQrSecret] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const { closeDrawer } = useDrawerContext();
  const { mutate: confirmPresenceQuery, isPending: isConfirmingPresence } = useConfirmPresence();
  const { mutate: verifyMfaCode, isPending: isCheckingMfa } = useVerifyMfa();

  const passwordForm = useForm({
    initialValues: {
      password: '',
    },
    validate: {
      password: (value) => validateRequired({ value, label: t('password_label') }),
    },
  });

  const completePresenceCheck = (result: ConfirmPresenceResult) => {
    setConfirmPresenceToken(result.confirmPresenceToken.token);
    if (result.mfaMethod) {
      const { secret, id } = result.mfaMethod;
      setQrSecret(`${secret.slice(0, 4)} ${secret.slice(4, 8)} ${secret.slice(8, 12)} ${secret.slice(12, 16)}`);
      setMethodId(id);
      setActiveStep(activeStep + 1);
    } else {
      displayNotificationError();
    }
  };

  const completeVerificationCallback = (result: RecoveryCodesMethod) => {
    setActiveStep(activeStep + 1);
    setRecoveryCodes(result.codes);
  };

  const isLoading = isCheckingMfa || isConfirmingPresence;

  const buttonGroup = (showConfirm?: { text: string }) => {
    return (
      <Group mt={'md'} justify={'flex-start'}>
        {showConfirm && <PortalButton type={'submit'} loading={isLoading} label={showConfirm.text} />}
        <PortalButton
          variant={'outline'}
          onClick={() => closeDrawer()}
          disabled={isLoading}
          label={t('cancel_label')}
        />
      </Group>
    );
  };

  return (
    <Stack gap={'md'}>
      <Title order={3}>{t('mfa_enable_title')}</Title>
      <Stepper color={'var(--mantine-color-turquoise-5)'} size={'sm'} active={activeStep}>
        <Stepper.Step label={t('mfa_enable_step_one')} description={t('mfa_enable_step_one_description')}>
          <Stack>
            <Text>{t('mfa_description')}</Text>
            <form
              onSubmit={passwordForm.onSubmit(() => {
                confirmPresenceQuery({
                  password: passwordForm.values.password,
                  userIdToAddTotp: userData?.user.id,
                  callback: completePresenceCheck,
                });
              })}
            >
              <PasswordInput placeholder={t('password_label')} {...passwordForm.getInputProps('password')} />
              {buttonGroup({ text: t('continue_label') })}
            </form>
          </Stack>
        </Stepper.Step>
        <Stepper.Step label={t('mfa_enable_step_two')} description={t('mfa_enable_step_two_description')}>
          <TwoStepAuthEnableList
            qrSecret={qrSecret}
            isLoading={isCheckingMfa}
            verifyCode={(sixDigitCode: string) =>
              verifyMfaCode({
                userId: userData?.user.id,
                methodId,
                presenceToken: confirmPresenceToken,
                sixDigitCode,
                completeVerificationCallback,
              })
            }
          />
          {buttonGroup()}
        </Stepper.Step>
        <Stepper.Completed>
          <Stack gap={'xs'} mb={'md'}>
            <Text>{t('mfa_recovery_paragraph_one')}</Text>
            <Text>
              <Trans i18nKey={'mfa_recovery_paragraph_two'} />
            </Text>
            <Text>
              <Trans i18nKey={'mfa_recovery_paragraph_three'} />
            </Text>
          </Stack>
          <Grid justify={'center'}>
            {recoveryCodes?.map((code) => (
              <Grid.Col key={`grid-col-${code}`} span={6}>
                <Text>{code}</Text>
              </Grid.Col>
            ))}
          </Grid>
        </Stepper.Completed>
      </Stepper>
    </Stack>
  );
};
